/**
 * Local persistence.
 *
 * Everything Namu remembers lives on the device. That is a product promise
 * made in the consent screen, so this module is deliberately the only place
 * that touches storage, and it never reaches for anything remote.
 *
 * Storage on a phone browser fails in ways that are easy to forget: private
 * mode throws on write, quota fills, another tab writes a newer shape, and a
 * user can clear it at any moment. None of those should be able to break a
 * conversation that is already in progress, so every operation degrades to a
 * working in-memory value rather than throwing.
 */

const NAMESPACE = 'namu'

export interface StoredEnvelope<T> {
  /** Schema version of the payload, for migration. */
  v: number
  /** When it was written, so stale data can be reasoned about. */
  at: number
  data: T
}

export type Migration = (data: unknown, fromVersion: number) => unknown

export interface StoreDefinition<T> {
  key: string
  version: number
  /** Used when nothing is stored, or when what is stored cannot be recovered. */
  fallback: () => T
  /** Runs when the stored version is older than `version`. */
  migrate?: Migration
  /** Last line of defence against corrupted or hand-edited storage. */
  validate?: (data: unknown) => data is T
}

function fullKey(key: string): string {
  return `${NAMESPACE}:${key}`
}

/** Whether persistence is available at all. Private mode can refuse writes. */
export function isStorageAvailable(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    const probe = `${NAMESPACE}:__probe__`
    localStorage.setItem(probe, '1')
    localStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}

/**
 * Reads a value, migrating and validating it on the way out.
 *
 * A value that fails validation is replaced with the fallback rather than
 * being handed to the app. Half-valid state is worse than no state: it shows
 * the user a name that is not theirs, or a consent flag they never set.
 */
export function load<T>(definition: StoreDefinition<T>): T {
  try {
    if (typeof localStorage === 'undefined') return definition.fallback()
    const raw = localStorage.getItem(fullKey(definition.key))
    if (raw === null) return definition.fallback()

    const parsed = JSON.parse(raw) as StoredEnvelope<unknown>
    if (typeof parsed !== 'object' || parsed === null || typeof parsed.v !== 'number') {
      return definition.fallback()
    }

    let data = parsed.data
    if (parsed.v < definition.version && definition.migrate) {
      data = definition.migrate(data, parsed.v)
    } else if (parsed.v > definition.version) {
      // Written by a newer build, in another tab or before a downgrade. The
      // shape is unknown, so it is not guessed at.
      return definition.fallback()
    }

    if (definition.validate && !definition.validate(data)) return definition.fallback()
    return data as T
  } catch (error) {
    console.warn(`Namu: could not read "${definition.key}" from storage`, error)
    return definition.fallback()
  }
}

/** Writes a value. Returns false when storage refused, without throwing. */
export function save<T>(definition: StoreDefinition<T>, data: T): boolean {
  try {
    if (typeof localStorage === 'undefined') return false
    const envelope: StoredEnvelope<T> = { v: definition.version, at: Date.now(), data }
    localStorage.setItem(fullKey(definition.key), JSON.stringify(envelope))
    return true
  } catch (error) {
    // Quota, private mode, or a disabled storage setting. A conversation in
    // progress must not stop because the phone will not remember it.
    console.warn(`Namu: could not write "${definition.key}" to storage`, error)
    return false
  }
}

export function remove(key: string): void {
  try {
    localStorage?.removeItem(fullKey(key))
  } catch (error) {
    console.warn(`Namu: could not clear "${key}"`, error)
  }
}

/**
 * Deletes everything Namu has stored. Wired to "delete everything" on the
 * profile screen, which is a promise the product makes out loud, so it clears
 * by prefix rather than by a list that could drift out of date.
 */
export function clearAll(): void {
  try {
    if (typeof localStorage === 'undefined') return
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith(`${NAMESPACE}:`)) doomed.push(key)
    }
    for (const key of doomed) localStorage.removeItem(key)
  } catch (error) {
    console.warn('Namu: could not clear stored data', error)
  }
}

/** When a value was last written, or null. Shown on the profile screen. */
export function savedAt(key: string): number | null {
  try {
    const raw = localStorage?.getItem(fullKey(key))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredEnvelope<unknown>
    return typeof parsed.at === 'number' ? parsed.at : null
  } catch {
    return null
  }
}
